import { useEffect, useState } from 'react';
import {
  Plus, Pencil, Trash2, RotateCcw, Building2, CheckCircle2, Package,
} from 'lucide-react';
import {
  useAdminTenants,
  useCreateAdminTenant,
  useUpdateAdminTenant,
  useAdminEntitlements,
  useGrantEntitlement,
  useRevokeEntitlement,
  PRODUCT_LABELS,
  type AdminTenant,
} from '../../lib/api/queries';
import {
  Modal, ModalHeader, ModalActions, IconButton, Field, Th, Td, inputCls,
} from './components';

/**
 * /admin/tenants — list, create, rename, soft-delete + restore.
 *
 * Soft-delete preserves audits + users + entitlements that still reference
 * the tenant. The list defaults to live tenants only; flip the toggle to
 * see deleted rows and a Restore action.
 */
export function AdminTenantsPage() {
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [creating, setCreating] = useState(false);
  const tenants = useAdminTenants({ includeDeleted });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Tenants</h1>
          <p className="text-sm text-gray-500">
            One tenant per DPAS customer. Soft-deletes keep historical audit
            data intact while hiding the tenant from pickers.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                     text-white bg-accent-alt-600 hover:bg-accent-alt-700 rounded-md
                     transition-colors"
        >
          <Plus className="w-4 h-4" aria-hidden />
          New tenant
        </button>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
        <input type="checkbox"
               checked={includeDeleted}
               onChange={e => setIncludeDeleted(e.target.checked)}
               className="rounded border-gray-300 text-accent-alt-600 focus:ring-accent-alt-500" />
        Show deleted tenants
      </label>

      <TenantTable tenants={tenants.data ?? []} loading={tenants.isLoading} />

      {creating && <CreateTenantModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function TenantTable({
  tenants, loading,
}: { tenants: AdminTenant[]; loading: boolean }) {
  const [renaming, setRenaming] = useState<AdminTenant | null>(null);
  const [entitling, setEntitling] = useState<AdminTenant | null>(null);
  const [confirming, setConfirming] = useState<
    { tenant: AdminTenant; action: 'delete' | 'restore' } | null
  >(null);
  const update = useUpdateAdminTenant();

  if (loading)         return <p className="text-sm text-gray-500">Loading tenants…</p>;
  if (!tenants.length) return <p className="text-sm text-gray-500">No tenants yet.</p>;

  const onConfirm = async () => {
    if (!confirming) return;
    await update.mutateAsync({
      id: confirming.tenant.id,
      delete:  confirming.action === 'delete'  ? true : undefined,
      restore: confirming.action === 'restore' ? true : undefined,
    });
    setConfirming(null);
  };

  return (
    <>
      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <Th>Name</Th>
              <Th>Created</Th>
              <Th>Status</Th>
              <th className="px-4 py-2 text-right font-medium w-32">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tenants.map(t => (
              <tr key={t.id} className={t.deletedAtUtc ? 'bg-gray-50/60' : 'hover:bg-gray-50'}>
                <Td>
                  <span className={'font-medium ' + (t.deletedAtUtc ? 'text-gray-500 line-through' : 'text-gray-900')}>
                    {t.name}
                  </span>
                </Td>
                <Td>
                  <span className="text-xs text-gray-500">
                    {new Date(t.createdAtUtc).toLocaleDateString()}
                  </span>
                </Td>
                <Td>
                  {t.deletedAtUtc ? (
                    <span className="text-xs text-danger-500">
                      Deleted {new Date(t.deletedAtUtc).toLocaleDateString()}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-success-600">
                      <CheckCircle2 className="w-3.5 h-3.5" aria-hidden /> Active
                    </span>
                  )}
                </Td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    {!t.deletedAtUtc && (
                      <>
                        <IconButton title="Manage entitlements"
                                    onClick={() => setEntitling(t)}>
                          <Package className="w-4 h-4" aria-hidden />
                        </IconButton>
                        <IconButton title="Rename" onClick={() => setRenaming(t)}>
                          <Pencil className="w-4 h-4" aria-hidden />
                        </IconButton>
                      </>
                    )}
                    {t.deletedAtUtc ? (
                      <IconButton title="Restore" onClick={() => setConfirming({ tenant: t, action: 'restore' })}>
                        <RotateCcw className="w-4 h-4" aria-hidden />
                      </IconButton>
                    ) : (
                      <IconButton title="Delete" danger
                                  onClick={() => setConfirming({ tenant: t, action: 'delete' })}>
                        <Trash2 className="w-4 h-4" aria-hidden />
                      </IconButton>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {entitling && (
        <EntitlementsModal tenant={entitling} onClose={() => setEntitling(null)} />
      )}
      {renaming && (
        <RenameTenantModal tenant={renaming} onClose={() => setRenaming(null)} />
      )}
      {confirming && (
        <ConfirmModal
          title={confirming.action === 'delete' ? 'Delete tenant' : 'Restore tenant'}
          body={
            confirming.action === 'delete'
              ? <>Delete <strong>{confirming.tenant.name}</strong>? Audits and users for this tenant stay in the database but it will no longer appear in pickers, and product endpoints will return 404 for its users.</>
              : <>Restore <strong>{confirming.tenant.name}</strong>? It will be visible again and its users can sign in.</>
          }
          confirmLabel={confirming.action === 'delete' ? 'Delete' : 'Restore'}
          danger={confirming.action === 'delete'}
          pending={update.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={onConfirm}
        />
      )}
    </>
  );
}

function CreateTenantModal({ onClose }: { onClose: () => void }) {
  const create = useCreateAdminTenant();
  const [name, setName] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    try {
      await create.mutateAsync({ name });
      onClose();
    } catch (err) {
      const detail = (err as { detail?: string })?.detail;
      const status = (err as { status?: number })?.status;
      setServerError(detail ?? (status ? `Request failed (${status})` : 'Request failed'));
    }
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader icon={<Building2 className="w-5 h-5 text-accent-alt-600" />} onClose={onClose}>
        Create tenant
      </ModalHeader>
      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="Name">
          <input type="text" required value={name}
                 onChange={e => setName(e.target.value)}
                 className={inputCls} placeholder="Acme Manufacturing Ltd" />
        </Field>
        {serverError && <p className="text-sm text-danger-500">{serverError}</p>}
        <ModalActions
          onCancel={onClose}
          confirmLabel={create.isPending ? 'Creating…' : 'Create tenant'}
          pending={create.isPending}
        />
      </form>
    </Modal>
  );
}

function EntitlementsModal({
  tenant, onClose,
}: { tenant: AdminTenant; onClose: () => void }) {
  const entitlements = useAdminEntitlements(tenant.id);
  const grant  = useGrantEntitlement();
  const revoke = useRevokeEntitlement();

  // Local optimistic selection so the toggle feels immediate. We diff
  // against the server state on Save and fire the minimum number of
  // grant/revoke calls.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!entitlements.data) return;
    setSelected(new Set(entitlements.data.map(e => e.productCode)));
  }, [entitlements.data]);

  const pending = grant.isPending || revoke.isPending;
  const initial = new Set((entitlements.data ?? []).map(e => e.productCode));
  const dirty =
    initial.size !== selected.size ||
    [...initial].some(p => !selected.has(p));

  const toggle = (code: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const save = async () => {
    const toGrant  = [...selected].filter(p => !initial.has(p));
    const toRevoke = [...initial].filter(p => !selected.has(p));
    for (const productCode of toGrant) {
      await grant.mutateAsync({ tenantId: tenant.id, productCode });
    }
    for (const productCode of toRevoke) {
      await revoke.mutateAsync({ tenantId: tenant.id, productCode });
    }
    onClose();
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader icon={<Package className="w-5 h-5 text-accent-alt-600" />}
                   onClose={onClose}>
        Entitlements
      </ModalHeader>
      <p className="text-xs text-gray-500 mb-3">
        {tenant.name} — toggle which products this tenant's users can reach.
        Changes take effect on the next request from any of their users.
      </p>

      {entitlements.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-2 mb-4">
          {Object.entries(PRODUCT_LABELS).map(([code, label]) => (
            <label key={code}
                   className="flex items-center justify-between gap-3 px-3 py-2
                              border border-gray-200 rounded-md hover:bg-gray-50
                              cursor-pointer select-none">
              <div>
                <div className="text-sm font-medium text-gray-900">{label}</div>
                <div className="text-xs text-gray-500 font-mono">{code}</div>
              </div>
              <input type="checkbox" checked={selected.has(code)}
                     onChange={() => toggle(code)}
                     disabled={pending}
                     className="rounded border-gray-300 text-accent-alt-600
                                focus:ring-accent-alt-500 w-4 h-4" />
            </label>
          ))}
        </div>
      )}

      <ModalActions
        onCancel={onClose}
        confirmLabel={pending ? 'Saving…' : dirty ? 'Save changes' : 'Close'}
        pending={pending}
        onConfirm={dirty ? save : onClose}
      />
    </Modal>
  );
}

function RenameTenantModal({
  tenant, onClose,
}: { tenant: AdminTenant; onClose: () => void }) {
  const update = useUpdateAdminTenant();
  const [name, setName] = useState(tenant.name);
  const [serverError, setServerError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    try {
      await update.mutateAsync({ id: tenant.id, name });
      onClose();
    } catch (err) {
      const detail = (err as { detail?: string })?.detail;
      const status = (err as { status?: number })?.status;
      setServerError(detail ?? (status ? `Request failed (${status})` : 'Request failed'));
    }
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader icon={<Pencil className="w-5 h-5 text-accent-alt-600" />} onClose={onClose}>
        Rename tenant
      </ModalHeader>
      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="Name">
          <input type="text" required value={name}
                 onChange={e => setName(e.target.value)}
                 className={inputCls} />
        </Field>
        {serverError && <p className="text-sm text-danger-500">{serverError}</p>}
        <ModalActions
          onCancel={onClose}
          confirmLabel={update.isPending ? 'Saving…' : 'Save'}
          pending={update.isPending}
        />
      </form>
    </Modal>
  );
}

function ConfirmModal({
  title, body, confirmLabel, danger, pending, onCancel, onConfirm,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal onClose={onCancel}>
      <ModalHeader onClose={onCancel}>{title}</ModalHeader>
      <p className="text-sm text-gray-700 mb-4">{body}</p>
      <ModalActions
        onCancel={onCancel}
        confirmLabel={pending ? 'Working…' : confirmLabel}
        pending={pending}
        danger={danger}
        onConfirm={onConfirm}
      />
    </Modal>
  );
}

